package mapsearch.zones

import apiclient.FormationClient
import apiclient.geoobjects.GeoObjectDetails
import apiclient.geoobjects.ObjectTags
import apiclient.geoobjects.ObjectType
import apiclient.geoobjects.SearchQueryContext
import apiclient.geoobjects.newContext
import apiclient.geoobjects.restSearch
import apiclient.tags.tag
import auth.WorkspacesStore
import dev.fritz2.core.RootStore
import dev.fritz2.core.storeOf
import koin.koinCtx
import koin.withKoin
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.combine
import mainmenu.AppStateStore
import model.AppPhase

class ZoneObjectLookUpStore : RootStore<Map<String, GeoObjectDetails>>(emptyMap(), Job()) {

    val workspacesStore: WorkspacesStore by koinCtx.inject()
    val zoneTilesLookUpStore: ZoneTilesLookUpStore by koinCtx.inject()
    val appStateStore: AppStateStore by koinCtx.inject()

    var fetching = storeOf(false, job)


    private suspend fun fetchAllZones(workspaceIds: List<String>? = null) {
        if (!fetching.current) {
            try {
                fetching.update(true)
                update(emptyMap())
                withKoin {
                    val client = get<FormationClient>()
                    (workspaceIds ?: workspacesStore.current.map { it.groupId }).let { groupIds ->
                        client.restSearch(
                            SearchQueryContext.newContext(groupIds).copy(
                                objectTypes = listOf(ObjectType.Zone),
                                excludeTags = listOfNotNull(
                                    ObjectTags.Deleted.tag(true),
                                    ObjectTags.Archived.tag(true),
                                ),
                                size = 1000,
                            ),
                        ).fold(
                            { results ->
                                zoneTilesLookUpStore.add(
                                    results.hits.associate { zoneResult ->
                                        zoneResult.hit.id to zoneResult.hit.latLon
                                    },
                                )
                                update(
                                    results.hits.associate { zoneResult ->
                                        zoneResult.hit.id to zoneResult.hit
                                    },
                                )
                            },
                            { error ->
                                console.error("Failed to fetch zones.", error.message, error.cause)
                            },
                        )
                    }
                }
            } finally {
                fetching.update(false)
            }
        }
    }

    fun upsertZone(zone: GeoObjectDetails) {
        upsertZones(mapOf(zone.id to zone))
    }

    val upsertZones = handle<Map<String, GeoObjectDetails>> { current, newZones ->
        val mutable = current.toMutableMap()
        newZones.entries.forEach { (zoneId, zone) ->
            zoneTilesLookUpStore.updateZoneInTileLookUp(zoneId = zone.id, zoneLatLon = zone.latLon)
            mutable[zoneId] = zone
        }
        mutable.toMap()
    }

    fun removeZone(zone: GeoObjectDetails) {
        removeZones(listOf(zone))
    }

    val removeZones = handle<List<GeoObjectDetails>> { current, zones ->
        zoneTilesLookUpStore.remove(zones.associate { it.id to it.latLon })
        current - zones.map { it.id }
    }

    init {
        combine(appStateStore.data, workspacesStore.data) { a, w -> Pair(a.appPhase, w) } handledBy { (appPhase, workspaces) ->
            if (appPhase == AppPhase.LoggedInAnonymous || appPhase == AppPhase.LoggedIn) {
                workspaces.mapNotNull { it.groupId.ifBlank { null } }
                    .ifEmpty { null }?.let { workspaceIds ->
                        fetchAllZones(workspaceIds)
                    }
            }
        }
    }

}
